import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { CheckCircle2, Monitor, ArrowRight } from 'lucide-react'
import api from '@/lib/api'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'

type Status = 'verifying' | 'success_same_device' | 'success_cross_device' | 'already' | 'error'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<Status>('verifying')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')

  // Match <html>/<body> background to the shell so iOS overscroll doesn't flash
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStatus('error')
        setMessage('No verification token provided')
        return
      }

      try {
        const response = await api.post('/api/auth/verify-email', { token })
        const data = response.data

        if (!data.success) {
          setStatus('error')
          setMessage(data.message || 'Verification failed')
          return
        }

        const verifiedEmail = data.email || ''
        if (verifiedEmail) setEmail(verifiedEmail)

        if (data.alreadyVerified) {
          setStatus('already')
          return
        }

        const localToken = localStorage.getItem('app_auth_token')
        if (!localToken) {
          setStatus('success_cross_device')
          return
        }

        try {
          const me = await api.get('/api/auth/me')
          const sameUser =
            verifiedEmail &&
            me.data?.agent?.email &&
            me.data.agent.email.toLowerCase() === verifiedEmail.toLowerCase()
          const onboardingIncomplete = !me.data?.onboardingComplete
          if (sameUser && onboardingIncomplete) {
            setStatus('success_same_device')
          } else {
            setStatus('success_cross_device')
          }
        } catch {
          setStatus('success_cross_device')
        }
      } catch (err: any) {
        setStatus('error')
        setMessage(err.response?.data?.message || 'This verification link is invalid or has expired.')
      }
    }

    run()
  }, [token])

  useEffect(() => {
    if (status !== 'success_same_device' && status !== 'already') return
    const hasToken = !!localStorage.getItem('app_auth_token')
    if (!hasToken) return
    const timer = setTimeout(() => navigate('/onboarding/step/2'), 2500)
    return () => clearTimeout(timer)
  }, [status, navigate])

  // ==========================================================================
  //   VERIFYING
  // ==========================================================================
  if (status === 'verifying') {
    return (
      <AuthShell
        headline={<>One second<br />while we verify.</>}
        sub="Checking your verification link against the account it was issued for."
      >
        <div className="flex items-center gap-3 text-white/60">
          <LoadingSpinner size="sm" />
          <p className="text-sm">Verifying your email…</p>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   ERROR (invalid / expired token)
  // ==========================================================================
  if (status === 'error') {
    return (
      <AuthShell
        headline={<>This link<br />has expired.</>}
        sub="Verification links are valid for a limited time. Sign in and we'll send you a fresh one."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">Verification failed</h2>
          <p className="text-white/60 text-sm mb-6">{message}</p>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-xl text-[15px] font-semibold text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97]"
          >
            Back to sign in
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   CROSS-DEVICE SUCCESS (user clicked link on a different device)
  // ==========================================================================
  if (status === 'success_cross_device') {
    return (
      <AuthShell
        headline={<>Email<br />verified.</>}
        sub="You can close this tab. Head back to the device where you started signing up — it'll pick up where you left off."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-300 mb-4">
            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
            Verified
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">
            You're verified.
          </h2>
          {email && (
            <p className="text-white/60 text-sm mb-6 break-all">
              <span className="text-white/90">{email}</span>
            </p>
          )}

          <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-4 flex items-start gap-3 mb-6">
            <Monitor className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" strokeWidth={1.75} />
            <div className="text-sm leading-relaxed">
              <p className="text-emerald-100 font-medium mb-0.5">Close this tab when ready.</p>
              <p className="text-emerald-300/80">
                Your original signup device is still polling — it'll advance to the next step automatically.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('app_auth_token')
              localStorage.removeItem('app_agent_data')
              localStorage.removeItem('app_onboarding_complete')
              localStorage.removeItem('app_onboarding_paused')
              localStorage.removeItem('app_onboarding_step')
              window.location.href = '/login'
            }}
            className="inline-flex items-center gap-1.5 py-2 -my-2 text-white/55 hover:text-white text-sm font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
          >
            Or sign in on this device
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </AuthShell>
    )
  }

  // ==========================================================================
  //   SAME-DEVICE SUCCESS + ALREADY VERIFIED (auto-redirect)
  // ==========================================================================
  const title = status === 'already' ? 'Email already verified.' : 'Email verified.'
  const body = status === 'already'
    ? "You're confirmed. Taking you back to continue…"
    : "You're in. Taking you to set up your organization…"

  return (
    <AuthShell
      headline={<>You're<br />verified.</>}
      sub="Your email is confirmed. Redirecting you back to onboarding."
    >
      <div
        className="opacity-0 animate-fade-in-up"
        style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
      >
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-300 mb-4">
          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
          Verified
        </div>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">{title}</h2>
        {email && <p className="text-white/50 text-xs mb-4 break-all">{email}</p>}
        <p className="text-white/60 text-sm flex items-center gap-2">
          <LoadingSpinner size="sm" />
          {body}
        </p>
      </div>
    </AuthShell>
  )
}
